import React, { Component } from 'react';

import PropTypes from 'prop-types';

import CapterraPixel from 'common/ads/CapterraPixel';
import AJAX from 'common/AJAX';
import * as AuthRequests from 'common/auth/AuthRequests';
import AuthButton from 'common/AuthButton';
import ReCAPTCHAWidget from 'common/common/ReCAPTCHAWidget';
import { TrackEventContext } from 'common/containers/EventContainer';
import { LocationContext } from 'common/containers/RouterContainer';
import getCookies from 'common/getCookies';
import Button from 'common/inputs/Button';
import SubdomainInput from 'common/inputs/SubdomainInput';
import TextInput from 'common/inputs/TextInput';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Strings from 'common/Strings';
import Tappable from 'common/Tappable';
import parseJSON from 'common/util/parseJSON';
import withContexts from 'common/util/withContexts';
import validateInput from 'common/validateInput';

import 'css/components/auth/_CompanySignupForm.scss';

class CompanySignupForm extends Component {
  static propTypes = {
    location: PropTypes.object,
    onSuccess: PropTypes.func.isRequired,
    trackEvent: PropTypes.func,
    trialPlan: PropTypes.oneOf(['starter', 'growth']),
  };

  state = {
    companyName: '',
    error: null,
    formOpen: !!this.props.location.query.email,
    oauthData: null,
    submitting: false,
  };

  constructor(props) {
    super(props);

    this.captchaWidgetRef = React.createRef();
    this.nameRef = React.createRef();
    this.emailRef = React.createRef();
    this.companyRef = React.createRef();
    this.passwordRef = React.createRef();
    this.subdomainRef = React.createRef();
  }

  onCompanyNameChange = (e) => {
    this.setState({
      companyName: e.target.value,
    });
  };

  onOAuthFailure = (error) => {
    this.setState({
      error,
    });
  };

  onOAuthSuccess = async (oauthData) => {
    const { error, parsedResponse, redirecting } = await AuthRequests.login(oauthData);

    // Note: We'd like to show register form if we receive an error from login endpoint except 'slow down'
    if (error && error.type === 'slow down') {
      this.setState({
        error: error.message,
      });
      return;
    }

    if (redirecting) {
      return;
    }

    if (parsedResponse?.user) {
      const { companies } = parsedResponse.user;
      if (companies.length > 0) {
        this.props.onSuccess({ companies });
        return;
      }
    }

    this.setState({
      formOpen: true,
      oauthData,
    });
  };

  onSubmit = async (e) => {
    e.preventDefault();

    if (this.state.submitting) {
      return;
    }

    const { oauthData } = this.state;

    const name = this.nameRef.current.getValue().trim();
    const email = this.emailRef.current.getValue().trim();
    const companyName = this.companyRef.current.getValue().trim();
    const subdomain = this.subdomainRef.current.getValue().trim();
    const password = oauthData ? null : this.passwordRef.current.getValue();

    const { trackEvent, trialPlan } = this.props;
    trackEvent('Submitted Register Form', {
      companyName,
      email,
      name,
      subdomain,
    });

    var error = null;
    if (!name || !validateInput.userName(name)) {
      error = 'Please enter a valid name.';
    } else if (!email || !validateInput.email(email)) {
      error = 'Please enter a valid email.';
    } else if (!companyName || !validateInput.companyName(companyName)) {
      error = 'Please enter a valid company name.';
    } else if (!subdomain || !validateInput.subdomain(subdomain)) {
      error = 'Please enter a valid subdomain.';
    } else if (!oauthData && (!password || !validateInput.password(password))) {
      error = 'Please enter a valid password.';
    }

    if (error) {
      trackEvent('Failed to submit register form', {
        error,
      });
      this.setState({ error });
      return;
    }

    trackEvent('Completed Registration');
    trackEvent('Made Register Request');

    this.setState({
      error: null,
      submitting: true,
    });

    this._subdomain = subdomain;
    const requestData = {
      ...oauthData,
      ...(!oauthData && { captcha: await this.captchaWidgetRef?.current.getCAPTCHA() }),
      companyName,
      email,
      hubspotutk: getCookies()?.hubspotutk,
      name,
      password,
      subdomain,
      trialPlan,
    };

    AJAX.post('/api/company/register', requestData, this.onSubmitResponse);
  };

  onSubmitResponse = (responseJSON) => {
    parseJSON(responseJSON, (error, responseObject) => {
      if (error) {
        this.setState({
          error: Strings.miscError,
        });
        return;
      }

      const { trackEvent } = this.props;

      if (responseObject.success) {
        trackEvent('Registered Company');

        window.qp?.('track', 'CompleteRegistration');

        if (responseObject.isVIP) {
          trackEvent('Registered VIP Company');
        }

        if (responseObject.qualified) {
          trackEvent('Registered Qualified Company');
        }

        this.setState({
          success: true,
          qualified: responseObject.qualified,
          vip: responseObject.isVIP,
        });

        // so event can send
        setTimeout(() => {
          const subdomain = this._subdomain;
          this.props.onSuccess({ subdomain });
        }, 400);
        return;
      }

      const { error: responseError } = responseObject;

      trackEvent('Failed to Register', {
        error: responseError,
      });

      const errorMessage =
        {
          'email domain is not trusted':
            'Canny does not accept disposable email addresses. Please, use another email address.',
          'email exists':
            'This email is already associated with an account. Did you mean to log in instead?',
          'invalid captcha':
            'Our servers failed to verify your identity. Please wait a few minutes before trying again.',
          'missing captcha':
            "Our servers couldn't verify your identity. Please wait a few minutes before trying again.",
          'subdomain taken':
            'Sorry, this subdomain is already in use by someone else. Try something else.',
          'slow down':
            'You are creating too many accounts. Please wait a few minutes before trying again.',
          'password is too weak': 'Please enter a stronger password.',
        }[responseError] || Strings.miscError;

      if (responseError === 'invalid captcha') {
        this.captchaWidgetRef?.current.refresh();
      }

      this.setState({
        error: errorMessage,
        submitting: false,
      });
    });
  };

  onToggleEmailForm = () => {
    this.setState({
      formOpen: !this.state.formOpen,
    });
  };

  renderGoogleButton() {
    const { oauthData } = this.state;
    if (oauthData) {
      return null;
    }

    const { formOpen } = this.state;
    const divider = formOpen ? (
      <div className="divider">
        <div className="line" />
        <div className="or">or</div>
        <div className="line" />
      </div>
    ) : null;

    return (
      <div className="googleContainer">
        {divider}
        <AuthButton
          authType="google"
          className="googleButton"
          onFailure={this.onOAuthFailure}
          onSuccess={this.onOAuthSuccess}
          value="Continue with Google"
        />
      </div>
    );
  }

  renderAvatar() {
    const { oauthData } = this.state;
    if (!oauthData || !oauthData.avatarURL) {
      return null;
    }

    return (
      <LazyLoadedImage alt="avatar" className="avatar" loading="eager" src={oauthData.avatarURL} />
    );
  }

  renderEmailForm = () => {
    const { oauthData, submitting } = this.state;
    if (!this.state.formOpen) {
      return null;
    }

    const { location } = this.props;
    return (
      <form className="registerForm" onSubmit={this.onSubmit}>
        <TextInput
          autoFocus={!!(!oauthData || !oauthData.name)}
          defaultValue={oauthData && oauthData.name}
          disabled={submitting}
          key={oauthData ? 'oauth name' : 'default name'}
          name="full name"
          placeholder="Full Name"
          ref={this.nameRef}
        />
        <TextInput
          autoFocus={!!(oauthData && oauthData.name && !oauthData.email)}
          defaultValue={(oauthData && oauthData.email) || location.query.email}
          disabled={submitting}
          key={oauthData ? 'oauth email' : 'default email'}
          name="work email"
          placeholder="Work Email"
          ref={this.emailRef}
        />
        <TextInput
          autoFocus={!!(oauthData && oauthData.name && oauthData.email)}
          disabled={submitting}
          name="company name"
          onChange={this.onCompanyNameChange}
          placeholder="Company/App Name"
          ref={this.companyRef}
        />
        <SubdomainInput
          disabled={submitting}
          input={this.state.companyName}
          placeholder="Subdomain"
          ref={this.subdomainRef}
        />
        {this.renderPasswordInput()}
        {this.renderError()}
        <ReCAPTCHAWidget ref={this.captchaWidgetRef} />
        <Button
          buttonType="greenButton"
          className="submitButton"
          formButton={true}
          loading={submitting}
          type="submit"
          value="Create My Account"
        />
      </form>
    );
  };

  renderError() {
    if (!this.state.error) {
      return null;
    }

    return <div className="error">{this.state.error}</div>;
  }

  renderContinueLink() {
    if (this.state.formOpen) {
      return null;
    }

    return (
      <Tappable onTap={this.onToggleEmailForm}>
        <div className="emailToggle">Or continue with email</div>
      </Tappable>
    );
  }

  renderPasswordInput() {
    if (this.state.oauthData) {
      return null;
    }

    return (
      <TextInput
        disabled={this.state.submitting}
        name="password"
        placeholder="Password"
        ref={this.passwordRef}
        type="password"
      />
    );
  }

  renderPixel() {
    const { success, qualified, vip } = this.state;
    if (!success) {
      return null;
    }

    if (!qualified && !vip) {
      return null;
    }

    return <CapterraPixel />;
  }

  render() {
    return (
      <div className="companySignupForm">
        {this.renderAvatar()}
        {this.renderEmailForm()}
        {this.renderGoogleButton()}
        {this.renderContinueLink()}
        {this.renderPixel()}
      </div>
    );
  }
}

export default withContexts({
  location: LocationContext,
  trackEvent: TrackEventContext,
})(CompanySignupForm);
